import { useConfirm, useLoading } from '@genial/components';
import {
  BasketCustomerOrderEntity,
  ErrorListResponse,
  instanceOfErrorListResponse,
} from '@genial/services';
import { Utils, useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { useState } from 'react';
import { useRecoilValue, useResetRecoilState } from 'recoil';

import { useToastCreator } from '../../../hooks/useToastCreator';
import {
  BatchTicketResumeDetailTypeParam,
  SignedInNavigationScreenNavigationProp,
} from '../../../routes/types';
import { useBatchTicketActions } from '../../../store/batchTicket/actions';
import {
  basketCustomerOrderResumeState,
  customerCartStatusState,
  productsCartStatusState,
} from '../../../store/batchTicket/atom';
import { errorMessageBatchTicketRequestItem } from '../../BatchTicketRequests/utils';
import { cancelAdvisorBasketService, getCustomerBalance } from '../services';
import {
  calculateAllocatedBalance,
  errorBalanceUpdateMessage,
  successBalanceUpdateMessage,
  validatePercentage,
} from '../utils';

interface useBatchTicketResumeItemProps {
  item: BasketCustomerOrderEntity;
}

export const useBatchTicketResumeItem = ({ item }: useBatchTicketResumeItemProps) => {
  const { navigate, goBack } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { removeCustomerToBasketOrderResume } = useBatchTicketActions();
  const { hide, show } = useLoading();
  const basketCustomerOrderResumeData = useRecoilValue(basketCustomerOrderResumeState).data;
  const resetBasketCustomerOrderResume = useResetRecoilState(basketCustomerOrderResumeState);
  const resetCustomerCartStatus = useResetRecoilState(customerCartStatusState);
  const resetProductsCartStatus = useResetRecoilState(productsCartStatusState);

  const basketCustomerOrderResumeDataRemainsOneOrEmpty =
    basketCustomerOrderResumeData.all.length <= 1;

  const isMobile = useBreakpointValue({ base: true, sm: false });
  const isTablet = useBreakpointValue({ base: false, sm: true, lg: false });

  const detailsBasketItemButtonIsDisabled = false;

  const { confirm } = useConfirm();
  const { showToast, showToastFromError } = useToastCreator('toast-resume-batch-ticket-update');

  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [availableBalance, setAvailableBalance] = useState(
    item?.customer?.account?.availableBalance || 0
  );

  const handleConfirmRemoveCustomerToBasket = async () => {
    show({
      title: 'Aguarde estamos removendo o cliente sua cesta.',
      description: 'Este processo pode levar alguns segundos, não saia desta pagina.',
    });
    await removeCustomerToBasketOrderResume(item.basketCustomerOrderId);
    try {
      if (basketCustomerOrderResumeDataRemainsOneOrEmpty) {
        show({
          title: 'Aguarde estamos cancelando sua cesta.',
          description: 'Este processo pode levar alguns segundos, não saia desta pagina.',
        });
        await cancelAdvisorBasketService(item.basketAdvisorOrderId);
        resetBasketCustomerOrderResume();
        resetCustomerCartStatus();
        resetProductsCartStatus();
        goBack();
      }
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorMessageBatchTicketRequestItem);
    } finally {
      hide();
    }
  };

  const handleRemoveCustomer = () => {
    const confirmDescription = basketCustomerOrderResumeDataRemainsOneOrEmpty
      ? 'Esta ação removerá o único cliente da lista e cancelará a boleta.'
      : 'Está ação removerá este cliente desta boleta.';

    confirm(
      {
        isMobile: isMobile || isTablet,
        description: `${confirmDescription}\nDeseja continuar mesmo assim?`,
        title: 'Atenção',
        icon: 'Warning',
        iconColor: 'feedback.attention.default',
        confirmText: 'Continuar',
        confirmColor: 'feedback.error.default',
      },
      handleConfirmRemoveCustomerToBasket
    );
  };

  const handleDetailsCustomer = (selectedTab: BatchTicketResumeDetailTypeParam) => {
    navigate('BatchTicketResumeDetail', {
      type: selectedTab,
      basketCustomerOrderId: item.basketCustomerOrderId,
    });
  };

  const handleUpdateBalance = async () => {
    try {
      setIsLoadingBalance(true);

      const availableBalanceResponse = await getCustomerBalance(
        item?.customer?.account?.accountNumber
      );

      if (availableBalanceResponse !== availableBalance) {
        setAvailableBalance(availableBalanceResponse);
      }

      showToast(...successBalanceUpdateMessage, { duration: 3000 });
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorBalanceUpdateMessage);
    } finally {
      setIsLoadingBalance(false);
    }
  };

  const allocatedBalanceOffers = calculateAllocatedBalance(item?.customerOffers || []);
  const allocatedBalancePercentage = (allocatedBalanceOffers / availableBalance) * 100;
  const allocatedBalancePercentageFormatted = Utils.Mask.Percentage(
    validatePercentage(allocatedBalancePercentage)
  );
  const allocatedBalanceFormatted = Utils.Mask.Money(allocatedBalanceOffers);

  const remainingBalance = availableBalance - allocatedBalanceOffers;
  const remainingBalanceFormatted = Utils.Mask.Money(remainingBalance);
  const remainingBalancePercentage = (remainingBalance / availableBalance) * 100;
  const remainingBalancePercentageFormatted = Utils.Mask.Percentage(
    validatePercentage(remainingBalancePercentage)
  );

  const allocatedBalanceField = `${allocatedBalanceFormatted} | ${allocatedBalancePercentageFormatted}`;
  const remainingBalanceField = `${remainingBalanceFormatted} | ${remainingBalancePercentageFormatted}`;

  return {
    handleRemoveCustomer,
    handleDetailsCustomer,
    handleUpdateBalance,
    isLoadingBalance,
    detailsBasketItemButtonIsDisabled,
    availableBalance,
    allocatedBalanceField,
    remainingBalanceField,
  };
};
